"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useRouter } from "next/navigation";
import { RefreshCw } from "lucide-react";
import { saveItineraryEdit } from "@/app/lib/saveItineraryEdit";
import { fixEncodedLinks } from "@/app/lib/itinerary/fixLinks";

const GYG_SRC =
  "https://widget.getyourguide.com/dist/pa.umd.production.min.js";

// Re-append the GetYourGuide partner script so it re-scans for any newly
// injected [data-gyg-widget] divs after a day is regenerated.
function reinitGyg() {
  const s = document.createElement("script");
  s.src = GYG_SRC;
  s.async = true;
  s.defer = true;
  s.setAttribute("data-gyg-partner-id", "JQ8NY6A");
  document.body.appendChild(s);
}

// Append a hover-reveal "remove" button to each activity paragraph that lacks
// one. Appended (not prepended) so the Morning/Afternoon/Evening
// `strong:first-child` timeline-pill styling in itinerary.css is unaffected.
function injectRemoveButtons(scope: ParentNode) {
  scope.querySelectorAll("section p").forEach((p) => {
    if (p.querySelector(":scope > .remove-activity")) return;
    if (!p.textContent?.trim()) return;
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "remove-activity";
    btn.setAttribute("aria-label", "Remove this activity");
    btn.textContent = "×";
    p.appendChild(btn);
  });
}

// Serialize #itineraryOutput without our injected controls (toolbars + remove
// buttons) so the saved itinerary HTML stays clean.
function serializeItinerary(): string {
  const output = document.getElementById("itineraryOutput");
  if (!output) return "";
  const clone = output.cloneNode(true) as HTMLElement;
  clone
    .querySelectorAll(".day-actions-host, .remove-activity")
    .forEach((el) => el.remove());
  return clone.innerHTML;
}

type HostInfo = { id: string; index: number; host: HTMLElement };

// Per-day editing: injects a "Regenerate day" toolbar (with an optional refine
// input) and a per-activity remove button into each day <section>. Regenerating
// calls /api/plan-day and swaps just that day; removing deletes one activity.
// Every edit persists the whole itinerary via the shared save helper — the
// first edit against a DEFAULT slot mints a new UUID and navigates there, and
// subsequent edits update that same saved copy in place.
export default function ItineraryDayActions({
  city,
  itinid,
}: {
  city: string;
  itinid: string;
}) {
  const [hosts, setHosts] = useState<HostInfo[]>([]);
  const router = useRouter();
  const effectiveItinid = useRef(itinid);
  const navigated = useRef(false);

  const persist = useCallback(async () => {
    const { newItinId } = await saveItineraryEdit({
      city,
      itinid: effectiveItinid.current,
      itinerary: serializeItinerary(),
    });
    if (newItinId) {
      effectiveItinid.current = newItinId; // subsequent edits update in place
      if (!navigated.current) {
        navigated.current = true;
        setTimeout(() => router.replace(`/show/${city}/${newItinId}`), 1000);
      }
    }
  }, [city, router]);

  const persistRef = useRef(persist);
  persistRef.current = persist;

  useEffect(() => {
    const output = document.getElementById("itineraryOutput");
    if (!output) return;
    const sections = Array.from(output.querySelectorAll("section"));
    if (sections.length === 0) return;

    const created: HTMLElement[] = [];
    const list: HostInfo[] = sections.map((section, i) => {
      section.id = `day-${i + 1}`;
      let host = section.querySelector<HTMLElement>(
        ":scope > .day-actions-host"
      );
      if (!host) {
        host = document.createElement("div");
        host.className = "day-actions-host";
        section.insertBefore(host, section.firstChild);
        created.push(host);
      }
      injectRemoveButtons(section);
      return { id: section.id, index: i, host };
    });
    setHosts(list);

    // One delegated listener handles every activity's remove button.
    const onClick = (event: Event) => {
      const target = event.target as HTMLElement;
      const btn = target.closest(".remove-activity");
      if (!btn) return;
      event.preventDefault();
      event.stopPropagation();
      const p = btn.closest("p");
      if (p) {
        p.remove();
        persistRef.current();
      }
    };
    output.addEventListener("click", onClick);

    return () => {
      output.removeEventListener("click", onClick);
      created.forEach((h) => h.remove());
      output.querySelectorAll(".remove-activity").forEach((b) => b.remove());
    };
  }, []);

  const totalDays = hosts.length;

  const handleRegenerate = async (info: HostInfo, instruction: string) => {
    const res = await fetch("/api/plan-day", {
      method: "POST",
      body: JSON.stringify({
        city,
        day: info.index + 1,
        totalDays,
        instruction,
      }),
    });
    if (!res.ok) throw new Error("Regeneration request failed");
    const { html } = await res.json();
    const section = document.getElementById(info.id);
    if (!section || !html) throw new Error("Missing section or HTML");

    // Swap the returned <section>'s children into the live section, keeping our
    // portal host node intact so the toolbar (and its React tree) survives.
    const tmp = document.createElement("div");
    tmp.innerHTML = fixEncodedLinks(String(html).trim());
    const newSection = tmp.querySelector("section") || tmp;

    Array.from(section.childNodes).forEach((n) => {
      if (n !== info.host) section.removeChild(n);
    });
    Array.from(newSection.childNodes).forEach((n) => section.appendChild(n));

    injectRemoveButtons(section);
    reinitGyg();
    await persist();
  };

  return (
    <>
      {hosts.map((info) =>
        createPortal(
          <DayToolbar
            key={info.id}
            onRegenerate={(instruction) => handleRegenerate(info, instruction)}
          />,
          info.host
        )
      )}
    </>
  );
}

function DayToolbar({
  onRegenerate,
}: {
  onRegenerate: (instruction: string) => Promise<void>;
}) {
  const [instruction, setInstruction] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const run = async () => {
    setBusy(true);
    setDone(false);
    try {
      await onRegenerate(instruction.trim());
      setInstruction("");
      setDone(true);
      setTimeout(() => setDone(false), 2500);
    } catch {
      // best-effort; leave the day unchanged on failure
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mb-4 flex flex-wrap items-center justify-end gap-2 border-b border-dashed border-gray-200 pb-3 print:hidden">
      <input
        value={instruction}
        onChange={(e) => setInstruction(e.target.value)}
        placeholder="Refine this day (optional)…"
        disabled={busy}
        aria-label="Refine this day"
        className="min-w-0 flex-1 rounded-lg border border-gray-300 px-3 py-1.5 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-orange-400 sm:max-w-xs"
      />
      <button
        type="button"
        onClick={run}
        disabled={busy}
        className="inline-flex items-center gap-1.5 rounded-lg border border-orange-300 px-3 py-1.5 text-sm font-semibold text-orange-700 transition-colors hover:bg-orange-50 disabled:opacity-60 cursor-pointer"
      >
        <RefreshCw className={`h-4 w-4 ${busy ? "animate-spin" : ""}`} />
        {busy ? "Regenerating…" : done ? "Updated!" : "Regenerate day"}
      </button>
    </div>
  );
}
